import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import { SCROLL_WRAPPER_TYPES } from '../../../shared/constants';

import { ArrowLeft } from '../../atoms/icons/ArrowLeft';
import { ArrowRight } from '../../atoms/icons/ArrowRight';

import { isBrowser } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import Spacings from '../../../styles/Spacings';
import { bisColorsTheme } from '../../../styles/themes/colors';

interface Props extends PropsWithChildren {
  childWidth: number;
  childGapWidth?: number;
  scrollType?: string;
  children: ReactNode[];
}

interface ArrowProps {
  itemsAmount: number;
  show: boolean;
  width: number;
  color: string;
  orientation?: 'right' | 'left';
}

const ScrollArrow = ({
  itemsAmount,
  show,
  width,
  color,
  orientation = 'right',
}: ArrowProps) => {
  return (
    <ArrowWrapper
      amountOfItems={itemsAmount}
      arrowWidth={width}
      showOnAllDevices={show}
      backgroundColor={color}
      direction={orientation}
    >
      {orientation === 'left' ? (
        <StyledArrowLeft
          amountOfItems={itemsAmount}
          className="end animate-icon"
          color={bisColorsTheme.colors.abnGreen500}
        />
      ) : (
        <StyledArrowRight
          amountOfItems={itemsAmount}
          className="end animate-icon"
          color={bisColorsTheme.colors.abnGreen500}
        />
      )}
    </ArrowWrapper>
  );
};

const HorizontalScrollWrapper = ({
  childWidth,
  childGapWidth,
  scrollType,
  children,
}: Props) => {
  const [hideSrollIndicator, setHideScrollInditator] = useState(false);
  const [hideLeftSrollIndicator, setLeftHideScrollInditator] = useState(true);
  const [hasScrollBar, setHasScrollBar] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  let ticking = false;

  const throttleHorizontalScroll = (event: Event) => {
    // Since scroll events can fire at a high rate ... it is recommended to throttle the event using requestAnimationFrame(), setTimeout(), or a CustomEvent
    // see: https://developer.mozilla.org/en-US/docs/Web/API/Document/scroll_event
    if (isBrowser && !ticking) {
      const hasReachedEndOfContainer =
        (event.target as HTMLElement).scrollWidth -
          (event.target as HTMLElement).scrollLeft ===
        (event.target as HTMLElement).clientWidth;

      const isAtStartOfContainer =
        (event.target as HTMLElement).scrollLeft <= 0;

      window.requestAnimationFrame(() => {
        setHideScrollInditator(hasReachedEndOfContainer);
        setLeftHideScrollInditator(isAtStartOfContainer);
        ticking = false;
      });

      ticking = true;
    }
  };

  useEffect(() => {
    if (scrollContainerRef?.current) {
      const hasHorizontalScrollbar =
        scrollContainerRef.current.scrollWidth >
        scrollContainerRef.current.clientWidth;

      setHasScrollBar(hasHorizontalScrollbar);

      scrollContainerRef.current.addEventListener(
        'scroll',
        (event) => throttleHorizontalScroll(event),
        false
      );
      return () => {
        scrollContainerRef.current?.removeEventListener(
          'scroll',
          throttleHorizontalScroll,
          false
        );
      };
    }
  }, []);

  return (
    <OuterScrollContainer>
      <FullWidthWrapper>
        {!hideLeftSrollIndicator && (
          <ScrollArrow
            itemsAmount={children.length}
            show={scrollType === SCROLL_WRAPPER_TYPES.nav && hasScrollBar}
            width={childWidth}
            color={
              scrollType === SCROLL_WRAPPER_TYPES.nav
                ? colors.white
                : bisColorsTheme.colors.abnNeutral50
            }
            orientation="left"
          />
        )}
        <ScrollContainer
          itemWidth={childWidth}
          itemGap={childGapWidth}
          itemAlign={
            scrollType === SCROLL_WRAPPER_TYPES.nav ? 'start' : 'center'
          }
          amountOfItems={children.length}
          ref={scrollContainerRef}
        >
          {children}
        </ScrollContainer>
        {!hideSrollIndicator && (
          <ScrollArrow
            itemsAmount={children.length}
            show={scrollType === SCROLL_WRAPPER_TYPES.nav && hasScrollBar}
            width={childWidth}
            color={
              scrollType === SCROLL_WRAPPER_TYPES.nav
                ? colors.white
                : bisColorsTheme.colors.abnNeutral50
            }
          />
        )}
      </FullWidthWrapper>
    </OuterScrollContainer>
  );
};

export default HorizontalScrollWrapper;

const OuterScrollContainer = styled.div`
  position: relative;
  width: 100%;

  > * {
    grid-column: 2 / -2;
  }

  ${mq.from.breakpoint.M`
    display: block;
  `}
`;

const FullWidthWrapper = styled.div`
  grid-column: 1 / -1;
  height: 20px;
  overflow-y: hidden;
`;

const StyledArrowRight = styled(ArrowRight)<{ amountOfItems: number }>`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  ${({ amountOfItems }) =>
    amountOfItems > 2
      ? css`
          display: block;
        `
      : css`
          display: none;
        `}
`;

const StyledArrowLeft = styled(ArrowLeft)<{ amountOfItems: number }>`
  left: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

  ${({ amountOfItems }) =>
    amountOfItems > 2
      ? css`
          display: block;
        `
      : css`
          display: none;
        `}
`;

const ArrowWrapper = styled.div<{
  amountOfItems: number;
  arrowWidth: number;
  showOnAllDevices: boolean;
  backgroundColor: string;
  direction: string;
}>`
  display: block;

  ${({ amountOfItems }) =>
    amountOfItems > 2
      ? css`
          &:after {
            content: '';
            display: block;
            height: 100%;
            position: absolute;
            top: 0;
            width: ${(props: any) => props.arrowWidth}px;
            z-index: 1;
          }
        `
      : undefined}

  ${({ direction }) =>
    direction === 'left'
      ? css`
          &:after {
            background: linear-gradient(
              to left,
              rgba(0, 0, 0, 0),
              ${(props: any) => props.backgroundColor}
            );
            left: -3px;
          }
        `
      : css`
          &:after {
            background: linear-gradient(
              to right,
              rgba(0, 0, 0, 0),
              ${(props: any) => props.backgroundColor}
            );
            right: -3px;
          }
        `}

  ${mq.from.breakpoint.M`
    display: ${({ showOnAllDevices }: { showOnAllDevices: boolean }) =>
      showOnAllDevices ? 'block' : 'none'};
  `}
`;

const ScrollContainer = styled.div<{
  amountOfItems: number;
  itemWidth: number;
  itemGap?: number;
  itemAlign: string;
}>`
  display: grid;
  grid-column-gap: ${({ itemGap }) => (itemGap ? `${itemGap}px` : Spacings.L)};
  grid-template-rows: minmax(${({ itemWidth }) => itemWidth}, 1fr);
  overflow-x: scroll;
  padding: 0;
  place-content: start;

  ${({ amountOfItems, itemWidth }) =>
    amountOfItems &&
    itemWidth &&
    css`
      grid-template-columns: repeat(${amountOfItems}, ${itemWidth}px);
    `}

  ${({ amountOfItems }) =>
    amountOfItems > 2
      ? css`
          justify-content: start;
        `
      : css`
          justify-content: ${(props: any) => props.itemAlign};
        `}

  ${mq.from.breakpoint.M`
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    justify-content: ${({ itemAlign }: { itemAlign: string }) => itemAlign};
    padding: 0;
    padding-bottom: 20px;

    &:after {
      content: none;
    }
  `}

  ${mq.from.breakpoint.L`
    grid-column-gap: ${({ itemGap }: { itemGap?: number }) =>
      itemGap ? `${itemGap}px` : Spacings.XL};
  `}
`;
